/**
 * Frozen copy of the Sharetribe hosted assets (Console content: translations,
 * footer, top bar, branding, layout, listing config, landing/terms/privacy
 * pages). Captured with scripts described in the migration notes; version is
 * recorded in _index.json.
 *
 * On Firebase-catalog builds the hostedAssets duck serves these snapshots
 * instead of calling the Asset Delivery API, which removes the runtime
 * dependency on Sharetribe for all app-wide content.
 */

import translations from './content_translations.json';
import footer from './content_footer.json';
import topbar from './content_top-bar.json';
import branding from './design_branding.json';
import layout from './design_layout.json';
import userTypes from './users_user-types.json';
import categories from './listings_listing-categories.json';
import listingTypes from './listings_listing-types.json';
import listingFields from './listings_listing-fields.json';
import listingSearch from './listings_listing-search.json';
import transactionSize from './transactions_minimum-transaction-size.json';
import mapIntegration from './integrations_map.json';
import localization from './general_localization.json';
import landingPage from './content_pages_landing-page.json';
import termsOfService from './content_pages_terms-of-service.json';
import privacyPolicy from './content_pages_privacy-policy.json';
import pageAbout from './content_pages_about.json';
import pageContactUs from './content_pages_contact-us.json';
import pageForFlorists from './content_pages_for-florists.json';
import pageShippingPolicy from './content_pages_shipping-policy.json';
import pageReturnPolicy from './content_pages_return-policy.json';

export const SNAPSHOT_VERSION = 'UMxTAcCwpd8wus3k_y_RXg';

// path -> full Asset Delivery API payload ({ data: [...], included?, meta }).
// Paths missing from this map simply don't exist in the marketplace
// (user-fields, analytics, google-search-console, access-control were empty
// at capture time) — same as the live API returning nothing for them.
const payloads = {
  '/content/translations.json': translations,
  '/content/footer.json': footer,
  '/content/top-bar.json': topbar,
  '/design/branding.json': branding,
  '/design/layout.json': layout,
  '/users/user-types.json': userTypes,
  '/listings/listing-categories.json': categories,
  '/listings/listing-types.json': listingTypes,
  '/listings/listing-fields.json': listingFields,
  '/listings/listing-search.json': listingSearch,
  '/transactions/minimum-transaction-size.json': transactionSize,
  '/integrations/map.json': mapIntegration,
  '/general/localization.json': localization,
  '/content/pages/landing-page.json': landingPage,
  '/content/pages/terms-of-service.json': termsOfService,
  '/content/pages/privacy-policy.json': privacyPolicy,
  '/content/pages/about.json': pageAbout,
  '/content/pages/contact-us.json': pageContactUs,
  '/content/pages/for-florists.json': pageForFlorists,
  '/content/pages/shipping-policy.json': pageShippingPolicy,
  '/content/pages/return-policy.json': pageReturnPolicy,
};

const absolute = path => (path.charAt(0) !== '/' ? `/${path}` : path);

// Mimics sdk.assetsByAlias({ paths }) — one response covering all paths.
export const snapshotAssetsResponse = paths => {
  const data = [];
  const included = [];
  const seenIncluded = new Set();

  paths.forEach(path => {
    const payload = payloads[absolute(path)];
    if (!payload) return;
    (payload.data || []).forEach(entry => data.push(entry));
    (payload.included || []).forEach(inc => {
      const key = `${inc.type}/${inc.id?.uuid || inc.id}`;
      if (!seenIncluded.has(key)) {
        seenIncluded.add(key);
        included.push(inc);
      }
    });
  });

  return { data: { data, included, meta: { version: SNAPSHOT_VERSION } } };
};

// Mimics sdk.assetByAlias({ path }) — single-asset response. Unlike the
// batched endpoint, `data` here is the asset CONTENT itself (e.g. the page's
// { sections, meta } object), NOT a jsonAsset resource wrapper — PageBuilder
// reads `sections` straight from it. Rejects with a 404-shaped error for
// unknown paths so CMSPage fallbacks behave as before.
export const snapshotAssetResponse = path => {
  const payload = payloads[absolute(path)];
  if (!payload || !payload.data?.length) {
    const error = new Error(`Asset not in snapshot: ${path}`);
    error.status = 404;
    return Promise.reject(error);
  }
  return Promise.resolve({
    data: {
      data: payload.data[0]?.attributes?.data ?? payload.data[0],
      included: payload.included || [],
      meta: { version: SNAPSHOT_VERSION },
    },
  });
};
